import { isEmpty, isEmptyObject } from '@plato/web-util/object';
import { createActorContext } from '@xstate/react';
import { isEqual, pickBy } from 'lodash-es';
import type { DeepPartial } from 'react-hook-form';
import { and, assertEvent, assign, fromPromise, log, not, raise, setup, stateIn } from 'xstate';
import type { z } from 'zod';
import type {
    SalesDraftDefaultHeaderInput,
    SalesDraftDefaultHeaderOutput,
    SalesDraftDetailOutput,
    SalesDraftOrder,
    SalesDraftType,
    SubmitSalesDraftInput,
    UpdateSalesDraftInput,
} from '../../../server/types/drafts-types.ts';
import { trpcClient } from '../../../utils/trpc-client.ts';
import type { SelectedLineItemProduct } from '../types/normal-line-machine-types';
import { addNormalItemMachine, normalLineItemFormSchema } from './normal-item-machine';
import { addNoteItemMachine } from './note-line-item-machine';

export enum DraftCurrentStage {
    LINE_ITEMS,
    HEADER,
    SUMMARY,
}

export type DraftSchemas = {
    ORDER: z.ZodSchema<UpdateSalesDraftInput, DeepPartial<UpdateSalesDraftInput>>;
    QUOTE: z.ZodSchema<UpdateSalesDraftInput, DeepPartial<UpdateSalesDraftInput>>;
};

export const draftMachine = setup({
    types: {
        context: {} as {
            customerId: string;
            draftOrderId: string;
            selectedProduct?: SelectedLineItemProduct;
            customerName: string;
            currentStage: DraftCurrentStage;
            draftOrder: SalesDraftOrder;
            prevDraftOrder: SalesDraftOrder;
            clerkId?: string | null;
            defaults?: SalesDraftDefaultHeaderOutput;
            schemas: DraftSchemas;
            salesOrderId?: string | null;
            pendingHeaderUpdate?: UpdateSalesDraftInput;
            updateError: boolean;
        },
        input: {} as {
            customerId: string;
            draftOrderId: string;
            customerName: string;
            currentStage: DraftCurrentStage;
            draftOrder: SalesDraftOrder;
            clerkId?: string | null;
            schemas: DraftSchemas;
        },
        events: {} as
            | { type: 'delete.draft' }
            | { type: 'delete.emptyDraft' }
            | { type: 'initialValue.select' }
            | { type: 'initialValue.cancelSelection' }
            | {
                  type: 'initialValue.selected';
                  payload: {
                      customerId: string;
                      storageId: string;
                  };
              }
            | { type: 'lineItems.add.note' }
            | {
                  type: 'lineItems.add.normal';
                  payload: {
                      product: SelectedLineItemProduct;
                      quantity?: number;
                  };
              }
            | { type: 'lineItems.update.draftOrder'; payload: SalesDraftOrder }
            | {
                  type: 'lineItems.updating';
                  payload: {
                      line_item_id: string;
                  };
              }
            | {
                  type: 'lineItems.updated';
                  payload: {
                      line_item_id: string;
                  };
              }
            | { type: 'lineItems.cancel.addNormalItem' }
            | { type: 'lineItems.cancel.addNoteItem' }
            | { type: 'continue.to.header' }
            | { type: 'view.basket' }
            | { type: 'view.header' }
            | { type: 'set.type'; payload: string }
            | { type: 'update.header'; payload: UpdateSalesDraftInput }
            | { type: 'submit' },
        children: {} as {
            addNormalItem: 'addNormalItem';
            addNoteItem: 'addNoteItem';
        },
    },
    actions: {
        replaceUrlState: (
            _,
            params: {
                customerId: string;
                storageId: string;
            },
        ) => {
            history.replaceState(null, '', `?customerId=${params.customerId}&storageId=${params.storageId}`);
        },
        addNormalItem: assign(({ event }) => {
            assertEvent(event, 'lineItems.add.normal');

            return {
                selectedProduct: event.payload.product,
            };
        }),
    },
    guards: {
        isInitialValueValid: ({ context }) =>
            !!context.draftOrder.customer_id && !!context.draftOrder.storage_id,
        isCustomerSelected: ({ context }) => !!context.customerId,
        isValidToAddItem: ({ context }) => !!context.customerId && !!context.draftOrder.storage_id,
        isCartEmpty: ({ context }) => context.draftOrder.line_items.length === 0,
        areLineItemsValid: ({ context }) => {
            // validate items that are valid to sumbit
            const itemCount = context.draftOrder.line_items.filter((item) => item.type !== 'NOTE').length;

            // validate normal items again the schema
            const areNormalItemsValid = context.draftOrder.line_items
                .filter((item) => item.type === 'NORMAL')
                .every((item) => {
                    const result = normalLineItemFormSchema.safeParse(item.normal_line_item);
                    if (result.error) {
                        console.warn({ result, normal_line_item: item.normal_line_item });
                    }
                    return result.success;
                });

            return itemCount > 0 && areNormalItemsValid;
        },
        isValidToUpdateHeader: ({ context }) => context.draftOrder.line_items.length > 0,
        isHeaderStage: ({ context }) => context.currentStage === DraftCurrentStage.HEADER,
        isClerkIdValid: ({ context }) => !!context.clerkId,
        // TODO: fix validate this implementation, clerkId is required
        isValidToUpdateDefaults: () => {
            return true;
        },
        hasClerkId: ({ context }) => !!context.clerkId,
        isNewState: ({ context }) => context.draftOrder.state === 'NEW',
        isHeaderUpdateChanged: ({ context, event }) => {
            assertEvent(event, 'update.header');
            return !isEqual(event.payload, context.pendingHeaderUpdate);
        },
        canEdit: ({ context }) => context.draftOrder.state === 'NEW',
        isDateValid: ({ context }) => {
            const { draftOrder } = context;

            if (draftOrder.type === 'ORDER') {
                return !!draftOrder.delivery_date;
            }

            if (draftOrder.type === 'QUOTE') {
                return !!draftOrder.due_date;
            }

            return false;
        },
        areHeaderValuesValid: ({ context }) => {
            const schema =
                context.draftOrder.type === 'ORDER' ? context.schemas.ORDER : context.schemas.QUOTE;

            // validate the header values with the schema
            try {
                schema.parse(context.draftOrder);
                return true;
            } catch (error) {
                console.warn('Header values are not valid', error);
                return false;
            }
        },
        isLastUpdateSuccessful: ({ context }) => !context.updateError,
    },
    actors: {
        addNoteItem: addNoteItemMachine,
        addNormalItem: addNormalItemMachine,
        getHeaderDefaults: fromPromise<SalesDraftDefaultHeaderOutput, SalesDraftDefaultHeaderInput>(
            ({ input }) => trpcClient.platoUser.query.salesDraft.getSalesDraft.getDefaultHeader.query(input),
        ),
        updateHeader: fromPromise<SalesDraftDetailOutput, UpdateSalesDraftInput>(({ input }) =>
            trpcClient.platoUser.command.salesDraft.draft.update.mutate(input),
        ),
        submitDraft: fromPromise<SalesDraftDetailOutput, SubmitSalesDraftInput>(({ input }) =>
            trpcClient.platoUser.command.salesDraft.draft.submit.mutate(input),
        ),
        deleteDraft: fromPromise<void, { draft_order_id: string }>(({ input }) => {
            return trpcClient.platoUser.command.salesDraft.draft.delete.mutate(input);
        }),
    },
}).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QQE4EMBmAXAdASwgBswBiANzzAHccAjNWAazCwG0AGAXUVAAcB7WHix5+AOx4gAHogCMAFgCsORezUAmAByL5ATna7dOgDQgAnonmb2OTUvZ2AzLvUA2Q7IC+n06ky4CYnJKGgALMDQIMBQObiQQASERcUkZBFlFZVUNbT0DI3lTC3StFTU1RwB2Q0VZXO9fdGx8IlJYyUThUQl4tNd1IsQtTJwlHXl1dUVK+RnKhpA-ZsC22Ti+QS6U3rkpspydfRrC8yHHdXkcIx1XSq15dguFpYDWklZ1dYTN5J7QNNkjiy5WGeWOgwQikcshwjnsmlk7Fk+lkri8PkWTVw9CYLBIeDEXTQhAAasSAK5gHCwMDEADGbC4HR+3VSiHOmlhjlc7Ch7FusnUughqk5cNF8mcbg8rmeWLoDGYWHxhJExLJhEpODpaDEdNpAGVaWAGd12vFOr82QgOVyeXyBUKRa5XLZ4YjkUi0XL-ArccqCUTSRSqTT6VhIOaNklWTsbaVHNzeY5+ZVBcLTggEbo3aooddbo4fc0cUqcIQCWAAJIRgC2sBIFbE1brsBwkQgODE-AjUe+Me2-0Q00qtkqinUskR6iBE0cEMFDxw1U06kqj0nVnmGJefrLTZbYHrjcrNaPbY7Xf4KFrxL7ltjQ8hlVHmnHk+ns5nC-Ojlhk7TOwEVcN91GLbFFRYctT1bE9mzPescHJXgIDQCMcBeAB5FAohiJkLRZQdpDkQFXWhQFHk0TQXRdCFXBGOxVBfVwlETIsd3lUsoIPBCGx41skJQtCCSge9CL+Yjn1fd8p0eL950zFxRyFVchSU2ZV3AvduJg884MPRDkNQiMIDEgcJLSEcxwnWSZx0b9M2ozkV1kSoeVchxXHYxpfS43B+L0gLEJ1PVaXbCAIAAOWvW9CAQsytgs4cX2sj85PshTikReRXR0TRoXkVFbj0MCON8yD-N048grbEL9UIcKop7AyEqtOMrLfGzPwyhcDFHdgrCmIEHA8eQtL86D4NgulxBEMQtSwfgcHCSJolax9JInCEhSRUZMiqRwEX5dhE3GirJoMtsVhIKJiHQl51qIvpXNS2z5IXEDXRfPRzg03QeVlMqS3OmqWiCR6koQHlpK69K5wXN8YTXKwoRTSptEBnzgf9C7eLB1YvgfJ7h10GG0rs+HM2RIxl3HJEMkldxtyxiCcdBo9eCwMwbuNe6sQh61dFmWxuUneip3XbkEdJnBkbfF1x2RQ6zrZqq2w5rmMN5kSABEsRu8QqQJMh+GYLW7rAPX-AFuMFAUFR+jRDccv+hG7BUIXsiBf7rE0FX9zVnANbMc2WF1-XohQa8cF4Qg0IwGLQ4jK3sBtp87cuRRHenC53FcBddGhK5MgUFMkRRMagdZgOpvPRqROiiMDebfAxBNs2O0blr8OjRLrSnVccFc4egQH5EF0nHMrFL9GnN0TSq+0yra8QjsG5i4lm6NtvTapTuN7ius08kwVSjcRRdFcmdDvkW+fwuK4EUKh41yfxR-Z0lerrrABVISTJIFIWAWA0J72wNEAAFNkAAlDdTiINA7CCPH-YykYe79j7nGFwNgLgzklHUW+EwMzFDfDYbk6MLgX1cg8SuLMl64wErdMOYgoAp2VBAQ2rd25UiYcnfm6CiaQ2yjmHk6h+Rl0vnCTKchio4AMLfE62gJxIm8picqqsv5J3Dv4Egkdo6x3jonXhlt+GE3Ev3AaIjHjiLUJIyUC47gwjGMjSoo8rC0LUdjGul0tEsLYQAURQFHFAgDgGgPbOAlAUC1CwN3BNUGvDtHYECcE4+lk3yvW6pTYom5lDyKzpOawLFSp0JWrhEgNJcBc14GANJlgxG2EFIoKiBhJgvghCmOoQ83BWH0LyLy3ItJlOiCQIy4Thl4TMeZa0ExlAIimC0lwa5KgdLwaMbkhV0aiiopjTxuAJkVPJLQWswg6kIFmY0hZ1glntMzFoPY64oQzmqLMECHjdwTPLPwSIuswAYDQOSQgWAGwcJbsbXeOAYBYAABIRFwjrP5AKgWwDOaiQust-oXCMPPQsHTpw4DREYAabhEYKCGXC6IXyfl+MRYC4Fuign6LjlgBON5IUsFhatFACL-l0pRQI8xtt3B-iFP0PQF9qKuLxTTcc5xJhAS0LyclXLBLGV+by5FW8uEQqhZy+FtLkVnOqFPDyaYVwGGmNtfQnIxFQmKmKnkqiPkUpQKq4SNKNX0r0a6gxLLE66pdTypFwLUV9VsGIxMpNBTcktZmFM0xlyZHnocWok5lW4TdXNKAeqRmgu3twzNYAc2TOZNM22YbVwnWcGmGc9EVlxuRJyaiRg4SFSmJKd+i9PljKzcWhlwSY7MtZbWQtxbQ0y0rZGmtMb63FHOC4ORJ0phqQ-GudNlLYBHJOVgLNWrwVm03cc4QbDx2vgjdW6NdbtrUTIdoGYWhqi2OZns5aLrqRbuELu71g7DFssPduk9Aqy3pwreeqNtbY05IRH+R4tQNDkJ2eu11-7hApOvKEkB6F-CQJgXA30nyUNYDQyWgiwGT7CoxWK7FkrpEIC0ICORUxERyVuIhrtb7El+P1nm7VZtjGAamZgkDE6wPTqvXc9wlwXx5g7fyScTr5SfM46wiOjKfVDqMbzEx1sgNCZPqBqt4GZ3bXHJnKYA1bjjhAlUJDviVP+GIxh8J2Gom4edSq5TAS1Oooo6KrFErcV3NMyoczOVpPWe3BibsUR4DxBeKWvTaQAC0+dMwpcXeUTLmWyWLxWAltqT4JgLkKiLNi-JrCFQRB-LA+WNppEVXaZMqZ0wdLhBl8osz8qZHefAnGgY1TBk1JbPAxJ+BQFq8TZ8nJXLz3HKuOETSIQTCcYYeeqI9BvjhNV1uQYNSUh1iNwgY3tRHZpBACbkNHZDxXHNvBi3MxZ1HOKBwHoUTekXhNfrh29vDdG1AHA-AakSUEdaK7M3OrzbbYoEUDhcwOElC4dwl9ts1Qu9aCcOZOrk3epmVxlwVJ1FccPLOfsPsIM0Xl0jiW5D4OXFOZNF9HbaF6jLPKQoqhrkUZ2uh8TEGqm+yGAACmAMQEARJo7jNUV0Ps7CPIx-0batjaYlROhMKiUIUeB2DhLp8mhDAi36KiWoHkpZU0VrLGY1EMhbI3JrzRwd8Y68kqTUc647jjHomIpECNDrK-0OcNQevdlxPJz4h3nmsRO4BAYV07B0YB-+i+f6sgEYzAt7MO4cJSa33RDz0PeM14sK7lHxAb5+o5U6lnQupNUtZQAsueeiqcqVZmHbnxheoDRRvMSEvWYhajDsADXOswMj3xFfPaEh09euWom3vGPaRIIV78xIerkqj0TrRMWdiAlIN9UoYFxC888aJ8Ug2sKDQHnapwVySL4YSez0MiEceht9ZncBihEbkkTrmogp9R3i8YI9-Bl8XRV9TMcoMhC5ZgPovJldVwEQoRjc59GEtMIA2Fe9ICYMH0c9k8xFX9URqhRhCoJw9dswUxSdj8ACUC7okkiM1MMDHVGM3wcC0Q8CC4ExcghZxZp9c8X0Jle8OdLlmlrk2lX8ph-pi58wqIqgMhBl2MVVKde4b8ARL4bVmlSYUwcVbkoMpxJCcoZxrAqh8pbMjtqVWEDVgUMDoRXQlALgOd1xAQ0w8UMgh5f99Ah8GYTDvkxcPVg1YBiMMDVDZZ1DXEDBJVX9oR+9rMlBDBOkSk+C31jZiQCAAAZNWXvC5eZYQ1pZZPFB+aYKNX6LOOPP-Zobtf+dVPw5fBpGvCcVxfKVcV2ONKtVwliNEVQBWLbeQjNKIT1WAC-EyDI6wFQUgow2xbPWjLQaiWWXkEqNcRMREbnBIlVBfXwvlAI6-OrOQc4JxCcSUO4BwmQjpfpAlaiJQQULyDQ2zVY7NF1ZfLOEYi+VMAaQEVZXQxmVtGQ+jHrfDN9CZAYyAKwycPaOwg4lRJwuNSYGwT6TQnKeNZ9dzDNG44tDYpQrY9IHYkE-YznRwlPSE-QK4RMQCL-f6DXbojdD9HdcXTYybF8ZSCYBwZpfQlwaHO5awdQZcWYqwe0NEWfck5DSk1EjBZQmnNrKNFwdk6mJ0Nk3Q64c4nZF8Cg5YjNQjQEmkoRTE2w7Ew4iEqDYYowKYF5BQbQUo-ZDjbWLjYA9U60J+RrVQMYHKSDIYN5PacYAaC+PKWzYxNAyPa022TUvY+w8EvEnJdwDkh024E6F0KEJUxEylIA5Jegv09OQqe-LORMC4R4AhAYILKEV01NcYO1bwbwIAA */
    id: 'draft',
    context: ({ input }) => ({
        customerId: input.customerId,
        draftOrderId: input.draftOrderId,
        customerName: input.customerName,
        currentStage: input.currentStage,
        draftOrder: input.draftOrder,
        prevDraftOrder: input.draftOrder, // Initialize with the current draft order
        clerkId: input.clerkId,
        schemas: input.schemas,
        updateError: false,
    }),
    entry: [log('Draft Machine initialized')],
    initial: 'idle',
    states: {
        idle: {
            always: [
                {
                    guard: and(['isHeaderStage', 'isValidToUpdateHeader']),
                    target: '#draft.header',
                },
                {
                    guard: and(['isHeaderStage', 'isCartEmpty']),
                    target: '#draft.header.invalidLineItems',
                },
                {
                    target: '#draft.basket',
                },
            ],
            on: {
                'view.basket': {
                    target: '#draft.basket',
                },
                'view.header': {
                    target: '#draft.header',
                },
            },
        },
        basket: {
            type: 'parallel',
            states: {
                initialValueDialog: {
                    initial: 'closed',
                    states: {
                        closed: {},
                        open: {},
                    },
                },
                lineItems: {
                    initial: 'idle',
                    entry: [log('Line Items')],
                    states: {
                        idle: {
                            always: [
                                {
                                    description: 'Initial value is valid and cart is not empty',
                                    guard: and(['isInitialValueValid', 'isCartEmpty']),
                                    target: '#draft.basket.lineItems.empty',
                                },
                                {
                                    description: 'Initial value is not valid',
                                    guard: not('isInitialValueValid'),
                                    target: '#draft.basket.lineItems.initialValuePending',
                                },
                            ],
                        },
                        initialValuePending: {
                            tags: ['initialValuePending'],
                        },
                        empty: {
                            tags: ['lineItemsEmpty'],
                            initial: 'idle',
                            states: {
                                idle: {},
                                deletingDraft: {
                                    tags: ['deletingDraft'],
                                    invoke: {
                                        id: 'deleteDraft',
                                        src: 'deleteDraft',
                                        input: ({ context }) => ({
                                            draft_order_id: context.draftOrderId,
                                        }),
                                        onDone: {
                                            target: 'deletedDraft',
                                        },
                                        onError: {
                                            target: 'deletingDraftError',
                                        },
                                    },
                                },
                                deletedDraft: {},
                                deletingDraftError: {
                                    after: {
                                        500: 'idle',
                                    },
                                },
                            },
                            on: {
                                'delete.emptyDraft': {
                                    guard: 'isCartEmpty',
                                    target: '.deletingDraft',
                                },
                            },
                        },
                        addingNote: {
                            tags: ['addingItem'],
                            invoke: {
                                id: 'addNoteItem',
                                src: 'addNoteItem',
                                input: ({ context }) => ({
                                    draft_order_id: context.draftOrderId,
                                }),
                                onDone: {
                                    target: 'idle',
                                    actions: assign(({ event }) => {
                                        if (event.output.draftOrder) {
                                            return {
                                                draftOrder: event.output.draftOrder,
                                            };
                                        }

                                        return {};
                                    }),
                                },
                            },
                        },
                        addingNormal: {
                            invoke: {
                                id: 'addNormalItem',
                                src: 'addNormalItem',
                                input: ({ context, event }) => {
                                    assertEvent(event, 'lineItems.add.normal');

                                    const { draftOrder, customerId, draftOrderId } = context;
                                    const { payload } = event;

                                    return {
                                        selectedProduct: payload.product,
                                        quantity: payload.quantity,
                                        draftOrderId,
                                        customerId,
                                        storageId: draftOrder.storage_id ?? '',
                                        shippingTypeId: draftOrder.shipping_type_id ?? '',
                                    };
                                },
                                onDone: {
                                    target: 'idle',
                                    actions: assign({
                                        draftOrder: ({ event }) => event.output.newDaftOrder,
                                    }),
                                },
                            },
                        },
                        updatingItem: {},
                        itemUpdated: {
                            after: {
                                500: 'idle',
                            },
                        },
                        deletingDraft: {
                            tags: ['deletingDraft'],
                            invoke: {
                                id: 'deleteDraft',
                                src: 'deleteDraft',
                                input: ({ context }) => ({
                                    draft_order_id: context.draftOrderId,
                                }),
                                onDone: {
                                    target: 'deletedDraft',
                                },
                                onError: {
                                    target: 'deletingDraftError',
                                },
                            },
                        },
                        deletedDraft: {
                            tags: ['deletedDraft'],
                            type: 'final',
                        },
                        deletingDraftError: {
                            after: {
                                500: 'idle',
                            },
                        },
                    },
                    on: {
                        'lineItems.add.note': {
                            guard: 'isNewState',
                            target: '.addingNote',
                        },
                        'lineItems.add.normal': {
                            guard: 'isNewState',
                            target: '.addingNormal',
                            actions: 'addNormalItem',
                        },
                        'lineItems.update.draftOrder': {
                            actions: [
                                assign({
                                    draftOrder: ({ event }) => event.payload,
                                }),
                            ],
                        },
                        'lineItems.updating': {
                            target: '.updatingItem',
                        },
                        'lineItems.updated': {
                            target: '.itemUpdated',
                        },
                        'lineItems.cancel.addNormalItem': {
                            target: '.idle',
                        },
                        'lineItems.cancel.addNoteItem': {
                            target: '.idle',
                        },
                        'continue.to.header': {
                            guard: 'areLineItemsValid',
                            target: '#draft.header',
                        },
                        'delete.draft': {
                            target: '.deletingDraft',
                        },
                    },
                },
            },
            on: {
                'initialValue.select': {
                    target: '.initialValueDialog.open',
                },
                'initialValue.cancelSelection': {
                    target: '.initialValueDialog.closed',
                },
                'initialValue.selected': {
                    actions: [
                        {
                            type: 'replaceUrlState',
                            params: ({ event }) => ({
                                customerId: event.payload.customerId,
                                storageId: event.payload.storageId,
                            }),
                        },
                        assign({
                            customerId: ({ event }) => event.payload.customerId,
                        }),
                    ],
                    target: '.lineItems',
                },
            },
        },
        header: {
            initial: 'loadingDefaults',
            states: {
                idle: {
                    on: {
                        submit: {
                            guard: and([
                                'areLineItemsValid',
                                'hasClerkId',
                                'isInitialValueValid',
                                'areHeaderValuesValid',
                                'isNewState',
                                'isLastUpdateSuccessful',
                            ]),
                            target: 'submitting',
                        },
                    },
                },
                loadingDefaults: {
                    invoke: {
                        id: 'getHeaderDefaults',
                        src: 'getHeaderDefaults',
                        input: ({ context }) => ({
                            clerk_id: context.clerkId ?? '',
                            sales_order_type: context.draftOrder.type,
                            customer_id: context.customerId,
                            external: {},
                        }),
                        onDone: {
                            actions: [
                                assign(({ event }) => ({
                                    defaults: event.output,
                                })),
                            ],
                            target: 'idle',
                        },
                        onError: {
                            target: 'loadingDefaultsError',
                        },
                    },
                },
                loadingDefaultsError: {},
                invalidLineItems: {},
                updatingDefaults: {
                    invoke: {
                        id: 'getHeaderDefaults',
                        src: 'getHeaderDefaults',
                        input: ({ context }) => ({
                            clerk_id: context.clerkId ?? '',
                            sales_order_type: context.draftOrder.type,
                            customer_id: context.customerId,
                            external: {},
                        }),
                        onDone: {
                            actions: [
                                assign(({ event }) => ({
                                    defaults: event.output,
                                })),
                                raise(({ event, context }) => {
                                    const defaults = event.output;

                                    return {
                                        type: 'update.header',
                                        payload: {
                                            draft_order_id: context.draftOrderId,
                                            type: context.draftOrder.type,
                                            shipping_type_id: defaults.shipping_type,
                                            billing_address: defaults.invoice_address,
                                            shipping_address: defaults.shipping_address,
                                            storage_id: defaults.storage_id,
                                            due_date: defaults.due_date,
                                            delivery_date: defaults.desired_delivery_date,
                                            external: {
                                                ...context.draftOrder.external,
                                                document_type: defaults.sales_order_type,
                                            },
                                        },
                                    };
                                }),
                            ],
                        },
                        onError: {
                            target: 'updatingDefaultsError',
                        },
                    },
                },
                defaultsUpdated: {},
                updatingDefaultsError: {},
                debouncingHeaderUpdate: {
                    after: {
                        500: 'updatingHeader',
                    },
                    on: {
                        'update.header': {
                            target: 'debouncingHeaderUpdate',
                            actions: assign({
                                pendingHeaderUpdate: ({ event }) => event.payload,
                            }),
                            reenter: true,
                        },
                    },
                },
                updatingHeader: {
                    invoke: {
                        id: 'updateHeader',
                        src: 'updateHeader',
                        input: ({ context }) => {
                            const { draftOrder, pendingHeaderUpdate } = context;

                            if (!pendingHeaderUpdate) {
                                throw new Error('No pending header update found');
                            }

                            const { line_items, ...rest } = draftOrder;
                            const currentDraftOrder = pickBy(rest, Boolean);

                            return {
                                ...currentDraftOrder,
                                ...pendingHeaderUpdate,
                            };
                        },
                        onDone: {
                            target: 'headerUpdated',
                            actions: assign({
                                draftOrder: ({ event }) => ({
                                    ...event.output.draft_order,
                                }),
                                prevDraftOrder: ({ event }) => ({
                                    ...event.output.draft_order,
                                }),
                                updateError: false,
                            }),
                        },
                        onError: {
                            target: 'updatingHeaderError',
                            actions: assign({
                                pendingHeaderUpdate: undefined,
                                updateError: true,
                            }),
                            // TODO: we need to control the form state
                            // actions: assign({
                            //     draftOrder: ({ context }) => ({
                            //         ...context.prevDraftOrder,
                            //     }),
                            // }),
                        },
                    },
                },
                headerUpdated: {
                    after: {
                        500: 'idle',
                    },
                },
                updatingHeaderError: {
                    after: {
                        500: 'idle',
                    },
                },
                submitting: {
                    invoke: {
                        id: 'submitDraft',
                        src: 'submitDraft',
                        input: ({ context }) => ({
                            draft_order_id: context.draftOrderId,
                            clerk_id: context.clerkId ?? '',
                        }),
                        onDone: {
                            target: 'submited',
                            actions: assign({
                                draftOrder: ({ event }) => event.output.draft_order,
                            }),
                        },
                        onError: {
                            target: 'submitError',
                        },
                    },
                },
                submitError: {
                    after: {
                        500: 'idle',
                    },
                },
                submited: {
                    type: 'final',
                },
                deletingDraft: {
                    tags: ['deletingDraft'],
                    invoke: {
                        id: 'deleteDraft',
                        src: 'deleteDraft',
                        input: ({ context }) => ({
                            draft_order_id: context.draftOrderId,
                        }),
                        onDone: {
                            target: 'deletedDraft',
                        },
                        onError: {
                            target: 'deletingDraftError',
                        },
                    },
                },
                deletedDraft: {
                    tags: ['deletedDraft'],
                    type: 'final',
                },
                deletingDraftError: {
                    after: {
                        500: 'idle',
                    },
                },
                updatingType: {
                    initial: 'gettingDefaults',
                    states: {
                        gettingDefaults: {
                            invoke: {
                                id: 'getHeaderDefaults',
                                src: 'getHeaderDefaults',
                                input: ({ context }) => ({
                                    clerk_id: context.clerkId ?? '',
                                    sales_order_type: context.draftOrder.type,
                                    customer_id: context.customerId,
                                    external: {},
                                }),
                                onDone: {
                                    actions: [
                                        assign(({ event }) => ({
                                            defaults: event.output,
                                        })),
                                    ],
                                    target: 'updatingHeader',
                                },
                                onError: {
                                    target: '#draft.header.idle',
                                },
                            },
                        },
                        updatingHeader: {
                            invoke: {
                                id: 'updateHeaderForType',
                                src: 'updateHeader',
                                input: ({ context }) => {
                                    const defaults = context.defaults;
                                    if (!defaults) {
                                        throw new Error('No defaults found');
                                    }

                                    const { line_items, ...rest } = context.draftOrder;
                                    const currentDraftOrder = pickBy(rest, Boolean);

                                    return {
                                        ...currentDraftOrder,
                                        draft_order_id: context.draftOrderId,
                                        type: context.draftOrder.type,
                                        shipping_type_id: defaults.shipping_type,
                                        billing_address: defaults.invoice_address,
                                        shipping_address: isEmptyAddress(defaults.shipping_address)
                                            ? defaults.invoice_address
                                            : defaults.shipping_address,
                                        storage_id: defaults.storage_id,
                                        due_date: defaults.due_date,
                                        delivery_date: defaults.desired_delivery_date,
                                        external: {
                                            ...context.draftOrder.external,
                                            document_type: defaults.sales_order_type,
                                        },
                                    };
                                },
                                onDone: {
                                    target: 'completing',
                                    actions: assign({
                                        draftOrder: ({ event }) => ({
                                            ...event.output.draft_order,
                                        }),
                                        prevDraftOrder: ({ event }) => ({
                                            ...event.output.draft_order,
                                        }),
                                        pendingHeaderUpdate: undefined,
                                        updateError: false,
                                    }),
                                },
                                onError: {
                                    target: '#draft.header.idle',
                                    actions: assign({
                                        updateError: true,
                                    }),
                                },
                            },
                        },
                        completing: {
                            after: {
                                500: '#draft.header.idle',
                            },
                        },
                    },
                },
            },
            on: {
                'set.type': {
                    guard: 'isValidToUpdateDefaults',
                    actions: assign({
                        draftOrder: ({ context, event }) => ({
                            ...context.draftOrder,
                            type: event.payload as SalesDraftType,
                        }),
                    }),
                    target: '.updatingType',
                },
                'update.header': {
                    guard: and([
                        'isNewState',
                        'isHeaderUpdateChanged',
                        not(stateIn('#draft.header.updatingType')),
                    ]),
                    actions: assign({
                        pendingHeaderUpdate: ({ event }) => event.payload,
                    }),
                    target: '.debouncingHeaderUpdate',
                },
                'delete.draft': {
                    target: '.deletingDraft',
                },
            },
        },
    },
});

export const DraftMachineContext = createActorContext(draftMachine, {});

function isEmptyAddress(value: object): boolean {
    return isEmptyObject(value, (prop) => prop === '' || isEmpty(prop));
}
